"use client";
import { useState, useRef } from "react";
import axios from "axios";
import Select from "react-select";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

// ── Extract all text from a PDF File using pdf.js (runs in browser) ──────────
// ── Extract all text from a PDF File using pdf.js (runs in browser) ──────────
// Rebuilds visual rows from item Y positions so each invoice row stays on its
// own line (the backend chunks on line boundaries).
async function extractPdfText(file) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const pages = [];

  const flush = (parts, lines) => {
    if (!parts.length) return;
    const line = parts.join(" ").replace(/\s+/g, " ").trim();
    if (line) lines.push(line);
  };

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();

    const lines = [];
    let current = [];
    let lastY = null;

    for (const item of content.items) {
      if (typeof item.str !== "string") continue;
      const y = item.transform?.[5] ?? 0;

      // New row when the baseline moves more than a few units
      if (lastY !== null && Math.abs(y - lastY) > 3 && current.length) {
        flush(current, lines);
        current = [];
      }

      if (item.str.trim()) current.push(item.str);
      lastY = y;

      if (item.hasEOL && current.length) {
        flush(current, lines);
        current = [];
        lastY = null;
      }
    }
    flush(current, lines);

    pages.push(lines.join("\n"));
  }

  return pages.join("\n");
}

// ─────────────────────────────────────────────────────────────────────────────

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const cls = (...c) => c.filter(Boolean).join(" ");

const selectStyles = {
  control: (p, s) => ({
    ...p,
    backgroundColor: "#111827",
    borderColor: s.isFocused ? "#f97316" : "#374151",
    color: "#e5e7eb",
    boxShadow: s.isFocused ? "0 0 0 2px rgba(249,115,22,0.2)" : "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "34px",
    fontSize: "13px",
    borderRadius: "0.375rem",
  }),
  input: (p) => ({ ...p, color: "#e5e7eb", margin: 0, padding: 0 }),
  singleValue: (p) => ({ ...p, color: "#f3f4f6", fontSize: "13px" }),
  placeholder: (p) => ({ ...p, color: "#6b7280", fontSize: "13px" }),
  option: (p, s) => ({
    ...p,
    backgroundColor: s.isSelected
      ? "#92400e"
      : s.isFocused
        ? "#1f2937"
        : "#111827",
    color: s.isSelected ? "#fde68a" : "#e5e7eb",
    fontSize: "13px",
    padding: "7px 12px",
  }),
  menu: (p) => ({
    ...p,
    backgroundColor: "#111827",
    border: "1px solid #1f2937",
    zIndex: 9999,
  }),
  menuList: (p) => ({ ...p, padding: "4px", maxHeight: "200px" }),
  valueContainer: (p) => ({ ...p, padding: "0 8px" }),
  indicatorsContainer: (p) => ({ ...p, padding: "0 4px" }),
  dropdownIndicator: (p) => ({ ...p, padding: "4px", color: "#4b5563" }),
  clearIndicator: (p) => ({ ...p, padding: "4px", color: "#4b5563" }),
  indicatorSeparator: (p) => ({ ...p, backgroundColor: "#1f2937" }),
  noOptionsMessage: (p) => ({ ...p, color: "#6b7280", fontSize: "13px" }),
  menuPortal: (p) => ({ ...p, zIndex: 9999 }),
};

const Pill = ({ children, colour }) => {
  const map = {
    green: "bg-emerald-900/60 text-emerald-300 border-emerald-700",
    red: "bg-red-900/60 text-red-300 border-red-700",
    amber: "bg-amber-900/60 text-amber-300 border-amber-700",
    gray: "bg-gray-800 text-gray-400 border-gray-700",
    indigo: "bg-indigo-900/60 text-indigo-300 border-indigo-700",
  };
  return (
    <span
      className={cls(
        "inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border",
        map[colour] ?? map.gray,
      )}
    >
      {children}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// StatementScanner
//
// Props:
//   v3Projects  — [{ _id, projectName }]  (same array as Bills3TablePage)
//   onClose     — fn()
// ─────────────────────────────────────────────────────────────────────────────
export default function StatementScanner({ v3Projects = [], onClose }) {
  const fileRef = useRef(null);

  const [file, setFile] = useState(null);
  const [projectId, setProjectId] = useState("");
  const [status, setStatus] = useState("idle"); // idle | extracting | parsing | scanning | done
  const [statusMsg, setStatusMsg] = useState("");
  const [result, setResult] = useState(null);
  const [aiMeta, setAiMeta] = useState(null); // { vendorName, statementDate, format }
  const [error, setError] = useState("");

  const [view, setView] = useState("all");
  const [search, setSearch] = useState("");

  const authH = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const projectOptions = v3Projects.map((p) => ({
    value: p._id,
    label: p.projectName,
  }));

  const reset = () => {
    setResult(null);
    setAiMeta(null);
    setError("");
    setStatus("idle");
    setStatusMsg("");
  };

  const handleFile = (e) => {
    setFile(e.target.files?.[0] || null);
    reset();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0] || null;
    if (f?.type === "application/pdf") {
      setFile(f);
      reset();
    }
  };

  const handleScan = async () => {
    if (!file) return;
    setError("");
    setResult(null);
    setAiMeta(null);

    try {
      // ── Step 1: Extract text in the browser via pdf.js ───────────────────
      setStatus("extracting");
      setStatusMsg("Reading PDF…");
      const rawText = await extractPdfText(file);

      if (!rawText.trim())
        throw new Error(
          "Could not extract text from PDF — may be a scanned image.",
        );

      // ── Step 2: Backend parses text with Groq → invoice numbers ──────────
      setStatus("parsing");
      setStatusMsg("AI extracting invoice numbers…");
      const parseRes = await axios.post(
        `${API}/bills3/parse-statement-text`,
        { text: rawText },
        { headers: authH() },
      );

      if (!parseRes.data.success)
        throw new Error(parseRes.data.message || "AI parsing failed");

      const { vendorName, statementDate, format, invoiceNumbers } =
        parseRes.data;
      setAiMeta({ vendorName, statementDate, format });

      if (!invoiceNumbers.length)
        throw new Error("No invoice numbers found in statement.");

      // ── Step 3: Backend looks up each number in bills3 ───────────────────
      setStatus("scanning");
      setStatusMsg(
        `Checking ${invoiceNumbers.length} invoices against system…`,
      );
      const scanRes = await axios.post(
        `${API}/bills3/scan-statement`,
        { invoiceNumbers, projectId: projectId || undefined },
        { headers: authH() },
      );

      if (!scanRes.data.success)
        throw new Error(scanRes.data.message || "Scan failed");

      setResult(scanRes.data);
      setView("all");
      setSearch("");
      setStatus("done");
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Scan failed");
      setStatus("idle");
    }
  };

  const rows = (result?.results || []).map((r, i) => ({ ...r, _idx: i + 1 }));
  const foundCount = rows.filter((r) => r.found).length;
  const missingCount = rows.filter((r) => !r.found).length;

  const filtered = rows.filter((r) => {
    if (view === "found" && !r.found) return false;
    if (view === "missing" && r.found) return false;
    if (search) {
      const q = search.toLowerCase();
      const inNum = r.invoiceNumber.toLowerCase().includes(q);
      const inName = (r.bill?.billName || "").toLowerCase().includes(q);
      const inProj = (r.bill?.project?.projectName || "")
        .toLowerCase()
        .includes(q);
      if (!inNum && !inName && !inProj) return false;
    }
    return true;
  });

  const exportMissing = () => {
    const missing = rows.filter((r) => !r.found);
    if (!missing.length) return;
    const lines = [
      "Invoice Number",
      ...missing.map((r) => `"${r.invoiceNumber}"`),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `missing_invoices_${(aiMeta?.vendorName || "statement").replace(/\s+/g, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const scanning = ["extracting", "parsing", "scanning"].includes(status);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-8 px-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl w-full max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <div>
            <h3 className="text-base font-semibold text-white">
              Statement Scanner
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Upload a vendor statement PDF to check which invoices are in the
              system
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-300 text-lg leading-none"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Drop zone */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            className={cls(
              "relative flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed cursor-pointer transition-colors py-8 px-4",
              file
                ? "border-orange-600/60 bg-orange-950/10"
                : "border-gray-700 hover:border-orange-600/50 bg-gray-800/30",
            )}
          >
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={handleFile}
            />
            {file ? (
              <>
                <span className="text-2xl">📄</span>
                <p className="text-sm font-medium text-orange-300">
                  {file.name}
                </p>
                <p className="text-xs text-gray-500">
                  {(file.size / 1024).toFixed(1)} KB — click to change
                </p>
              </>
            ) : (
              <>
                <span className="text-2xl text-gray-600">📂</span>
                <p className="text-sm text-gray-400">
                  Drop vendor statement PDF here, or{" "}
                  <span className="text-orange-400 underline">browse</span>
                </p>
                <p className="text-xs text-gray-600">
                  Supports Watson and Dass Metal formats
                </p>
              </>
            )}
          </div>

          {/* Project + scan button */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">
                Scope to Project{" "}
                <span className="text-gray-600 font-normal normal-case">
                  (optional)
                </span>
              </label>
              <Select
                options={[
                  { value: "", label: "All projects" },
                  ...projectOptions,
                ]}
                value={
                  projectOptions.find((o) => o.value === projectId) || {
                    value: "",
                    label: "All projects",
                  }
                }
                onChange={(s) => setProjectId(s?.value || "")}
                styles={selectStyles}
                placeholder="All projects…"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleScan}
                disabled={!file || scanning}
                className="w-full px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-orange-500 hover:bg-orange-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {scanning ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin text-base">⟳</span>
                    {statusMsg}
                  </span>
                ) : (
                  "🔍 Scan Statement"
                )}
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-lg bg-red-900/30 border border-red-700/50 px-4 py-3 text-sm text-red-300">
              {error}
            </div>
          )}

          {/* Results */}
          {result && aiMeta && (
            <div className="space-y-4">
              {/* Summary bar */}
              <div className="rounded-lg border border-gray-700 bg-gray-800/50 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-white">
                      {aiMeta.vendorName || "Unknown Vendor"}
                      {aiMeta.statementDate && (
                        <span className="ml-2 text-xs font-normal text-gray-400">
                          Statement: {aiMeta.statementDate}
                        </span>
                      )}
                      <span className="ml-2 text-[10px] font-normal text-gray-600 uppercase tracking-wide">
                        Format {aiMeta.format}
                      </span>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {result.totalOnStatement} invoice
                      {result.totalOnStatement !== 1 ? "s" : ""} on statement
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-center">
                      <p className="text-xl font-bold text-emerald-400">
                        {foundCount}
                      </p>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                        In System
                      </p>
                    </div>
                    <div className="w-px h-8 bg-gray-700" />
                    <div className="text-center">
                      <p className="text-xl font-bold text-red-400">
                        {missingCount}
                      </p>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                        Missing
                      </p>
                    </div>
                    <div className="w-px h-8 bg-gray-700" />
                    <div className="text-center">
                      <p className="text-xl font-bold text-gray-300">
                        {result.totalOnStatement > 0
                          ? Math.round(
                              (foundCount / result.totalOnStatement) * 100,
                            )
                          : 0}
                        %
                      </p>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                        Coverage
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-3 h-1.5 rounded-full bg-gray-700 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-emerald-500 transition-all"
                    style={{
                      width:
                        result.totalOnStatement > 0
                          ? `${(foundCount / result.totalOnStatement) * 100}%`
                          : "0%",
                    }}
                  />
                </div>
              </div>

              {/* Filter tabs + search + export */}
              <div className="flex flex-wrap items-center gap-2">
                {[
                  { key: "all", label: `All (${rows.length})` },
                  { key: "found", label: `In System (${foundCount})` },
                  { key: "missing", label: `Missing (${missingCount})` },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setView(t.key)}
                    className={cls(
                      "px-3 py-1.5 rounded-md text-xs font-medium border transition-colors",
                      view === t.key
                        ? "bg-orange-500 border-orange-500 text-white"
                        : "bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500",
                    )}
                  >
                    {t.label}
                  </button>
                ))}
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search invoice #…"
                  className="ml-auto w-48 rounded-md bg-gray-800 border border-gray-700 text-gray-200 text-xs py-1.5 px-2.5 focus:border-orange-500 focus:outline-none"
                />
                {missingCount > 0 && (
                  <button
                    onClick={exportMissing}
                    className="px-3 py-1.5 rounded-md text-xs font-medium border border-gray-700 text-gray-400 bg-gray-800 hover:border-gray-500 transition-colors"
                  >
                    ↓ Export Missing
                  </button>
                )}
              </div>

              {/* Table */}
              <div className="rounded-lg border border-gray-800 overflow-hidden">
                <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                  <table className="min-w-full text-xs">
                    <thead className="sticky top-0 bg-gray-900 z-10">
                      <tr className="border-b border-gray-800">
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400 w-8">
                          #
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                          Invoice #
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                          Status
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                          Bill Name
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                          Project
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                          MO
                        </th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                          Processing
                        </th>
                        <th className="px-3 py-2.5 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800/60">
                      {filtered.length === 0 ? (
                        <tr>
                          <td
                            colSpan={8}
                            className="px-4 py-8 text-center text-gray-500"
                          >
                            No results match your filter.
                          </td>
                        </tr>
                      ) : (
                        filtered.map((r) => (
                          <tr
                            key={r.invoiceNumber}
                            className={cls(
                              "transition-colors",
                              r.found
                                ? "hover:bg-gray-800/40"
                                : "bg-red-950/10 hover:bg-red-950/20",
                            )}
                          >
                            <td className="px-3 py-2 text-gray-600 tabular-nums">
                              {r._idx}
                            </td>
                            <td className="px-3 py-2 font-mono text-gray-200 whitespace-nowrap">
                              {r.invoiceNumber}
                            </td>
                            <td className="px-3 py-2">
                              {r.found ? (
                                <Pill colour="green">✓ Found</Pill>
                              ) : (
                                <Pill colour="red">✗ Missing</Pill>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-300 max-w-[160px]">
                              <div
                                className="truncate"
                                title={r.bill?.billName}
                              >
                                {r.bill?.billName || ""}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-gray-400 max-w-[130px]">
                              <div
                                className="truncate"
                                title={r.bill?.project?.projectName}
                              >
                                {r.bill?.project?.projectName || ""}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-gray-400 font-mono whitespace-nowrap">
                              {r.bill?.relatedMO3
                                ? `MO ${r.bill.relatedMO3.orderNumber}`
                                : ""}
                            </td>
                            <td className="px-3 py-2">
                              {r.bill?.processingStatus === "processed" ? (
                                <Pill colour="indigo">Processed</Pill>
                              ) : r.bill?.processingStatus === "pending" ? (
                                <Pill colour="amber">Pending</Pill>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-200 tabular-nums">
                              {r.bill?.total != null
                                ? `$${fmt(r.bill.total)}`
                                : ""}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-800">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-400 hover:text-gray-200 transition-colors"
          >
            Close
          </button>
          {file && !scanning && (
            <button
              onClick={handleScan}
              disabled={scanning}
              className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-orange-500 hover:bg-orange-400 disabled:opacity-50 transition-colors"
            >
              {result ? "Re-scan" : "Scan Statement"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
